import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Briefcase, MapPin, ArrowRight } from "lucide-react";
import { careersApi } from "../../services/careersApi";
import { useLocalization } from "../../hooks/useLocalization";
import { PublicHeader } from "../../components/PublicHeader";
import { JobPostingDetail } from "../../components/careers/JobPostingDetail";

export default function CareersPage() {
  const { t, language } = useLocalization();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    careersApi.listJobs().then((r) => setJobs(r.data)).finally(() => setLoading(false));
  }, []);

  const title = (job) => (language === "ja" ? job.title_ja : job.title_en) || job.title_en;
  const singleJob = jobs.length === 1 ? jobs[0] : null;

  return (
    <div className="min-h-screen w-full bg-white text-blue-950" data-testid="careers-page">
      <PublicHeader showCareersLink={false} />
      <header className="max-w-6xl mx-auto px-4 sm:px-6 pt-5 sm:pt-6 pb-5 sm:pb-6 border-b border-gray-100">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full bg-amber-400 text-blue-950 text-xs font-bold tracking-wide uppercase mb-3 sm:mb-4">
            <Briefcase size={14} />
            {t("nav.careers")}
          </span>
          <h1 data-testid="careers-title" className="text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-tight tracking-tight mb-2 sm:mb-3 break-words">
            {t("careers.title")}
          </h1>
          <p data-testid="careers-subtitle" className="text-sm sm:text-base text-gray-500 leading-relaxed">
            {t("careers.subtitle")}
          </p>
        </div>
      </header>

      {loading ? (
        <section className="min-h-[40vh] flex items-center justify-center">
          <p className="text-gray-500" data-testid="careers-loading">...</p>
        </section>
      ) : jobs.length === 0 ? (
        <section className="min-h-[40vh] flex items-center justify-center">
          <p className="text-gray-500 text-center py-10" data-testid="careers-no-jobs">{t("careers.noJobsOpen")}</p>
        </section>
      ) : singleJob ? (
        // Only one open position - show it in full directly on this page, no drill-down needed.
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <JobPostingDetail job={singleJob} />
        </section>
      ) : (
        <section className="bg-gray-50 py-10 sm:py-16 lg:py-20 min-h-[40vh]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="space-y-3 sm:space-y-4">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  data-testid={`careers-job-card-${job.id}`}
                  onClick={() => navigate(`/careers/jobs/${job.id}`)}
                  className="group cursor-pointer rounded-2xl border border-gray-100 bg-white p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 hover:-translate-y-0.5 hover:shadow-lg hover:border-amber-300 transition-all duration-300"
                >
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg font-bold text-blue-950 break-words">{title(job)}</h3>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-500 mt-1">
                      {job.store_location && (
                        <span className="flex items-center gap-1"><MapPin size={13} className="shrink-0" /> <span className="break-words">{job.store_location}</span></span>
                      )}
                      {job.employment_type && <span>{job.employment_type.replaceAll("_", " ")}</span>}
                    </div>
                  </div>
                  <Link
                    to={`/careers/jobs/${job.id}`}
                    data-testid={`careers-view-details-link-${job.id}`}
                    className="flex items-center justify-center gap-1.5 w-full sm:w-auto px-4 py-2.5 sm:py-2 rounded-full bg-blue-950 text-white text-sm font-semibold group-hover:bg-amber-400 group-hover:text-blue-950 transition-colors shrink-0"
                  >
                    {t("careers.viewDetails")} <ArrowRight size={14} />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
